import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker,
  message, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Statistic,
  Badge,
  Dropdown,
  Menu,
  Divider,
  Tabs
} from 'antd';
import { 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined,
  UserOutlined,
  SearchOutlined,
  DownloadOutlined,
  MoreOutlined,
  ReloadOutlined,
  DragOutlined,
  CalendarOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { theme } from '../../styles/theme';
import { trackAdminEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { SERVICE_FREQUENCIES, QUOTE_STATUSES, KANBAN_COLUMNS } from '../../constants/services';
import { formatDate, formatCurrency } from '../../utils/helpers';
const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const AdminDashboard = () => {
  const [quotes, setQuotes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [allInquiries, setAllInquiries] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    newInquiry: 0,
    quoteSent: 0,
    approved: 0,
    scheduled: 0,
    totalValue: 0
  });

  // Fetch quotes from Firebase
  useEffect(() => {
    const q = query(
      collection(db, 'quotes'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const quotesData = [];
      let totalValue = 0;
      let newInquiryCount = 0;
      let quoteSentCount = 0;
      let approvedCount = 0;
      let scheduledCount = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Map old statuses to new Kanban statuses
        let mappedStatus = data.status;
        if (data.status === 'pending') mappedStatus = 'new_inquiry';
        
        quotesData.push({
          id: doc.id,
          ...data,
          status: mappedStatus,
          type: 'quote',
          createdAt: data.createdAt?.toDate?.() || new Date(data.timestamp),
          updatedAt: data.updatedAt?.toDate?.() || new Date(data.timestamp)
        });

        // Calculate stats
        totalValue += data.quoteAmount || 0;
        if (mappedStatus === 'new_inquiry') newInquiryCount++;
        else if (mappedStatus === 'quote_sent') quoteSentCount++;
        else if (mappedStatus === 'approved') approvedCount++;
        else if (mappedStatus === 'scheduled') scheduledCount++;
      });

      setQuotes(quotesData);
      setStats({
        total: quotesData.length,
        newInquiry: newInquiryCount,
        quoteSent: quoteSentCount,
        approved: approvedCount,
        scheduled: scheduledCount,
        totalValue
      });
    });

    return () => unsubscribe();
  }, []);

  // Fetch appointments from Firebase
  useEffect(() => {
    const q = query(
      collection(db, 'appointments'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const appointmentsData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Map appointment status to kanban status
        let mappedStatus = 'new_inquiry'; // default
        if (data.status === 'scheduled') mappedStatus = 'new_inquiry';
        else if (data.status === 'confirmed') mappedStatus = 'quote_sent';
        else if (data.status === 'completed') mappedStatus = 'approved';
        else if (data.status === 'cancelled') mappedStatus = 'cancelled';
        
        appointmentsData.push({
          id: doc.id,
          ...data,
          status: mappedStatus,
          type: 'appointment',
          // Map appointment fields to quote fields for display
          email: data.customerEmail,
          phone: data.customerPhone,
          address: data.address || 'Not provided',
          quoteAmount: data.totalPrice || 0,
          services: data.serviceTypes || [],
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        });
      });

      setAppointments(appointmentsData);
    });

    return () => unsubscribe();
  }, []);

  // Fetch bookings from Firebase
  useEffect(() => {
    const q = query(
      collection(db, 'bookings'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const bookingsData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Map booking status to kanban status
        let mappedStatus = 'new_inquiry'; // default
        if (data.status === 'pending') mappedStatus = 'new_inquiry';
        else if (data.status === 'contacted') mappedStatus = 'quote_sent';
        else if (data.status === 'scheduled') mappedStatus = 'scheduled';
        else if (data.status === 'completed') mappedStatus = 'approved';
        else if (data.status === 'cancelled') mappedStatus = 'cancelled';
        
        bookingsData.push({
          id: doc.id,
          ...data,
          status: mappedStatus,
          type: 'booking',
          // Map booking fields to display fields
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || 'Not provided',
          quoteAmount: 0, // Bookings don't have amounts initially
          serviceFrequency: 'one-time',
          fullName: data.fullName || '',
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        });
      });

      setBookings(bookingsData);
    });

    return () => unsubscribe();
  }, []);

  // Combine quotes, appointments, and bookings and calculate stats
  useEffect(() => {
    const combined = [...quotes, ...appointments, ...bookings].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
    setAllInquiries(combined);
    
    // Calculate stats
    let totalValue = 0;
    let newInquiryCount = 0;
    let quoteSentCount = 0;
    let approvedCount = 0;
    let scheduledCount = 0;

    combined.forEach(item => {
      totalValue += item.quoteAmount || 0;
      if (item.status === 'new_inquiry') newInquiryCount++;
      else if (item.status === 'quote_sent') quoteSentCount++;
      else if (item.status === 'approved') approvedCount++;
      else if (item.status === 'scheduled') scheduledCount++;
    });

    setStats({
      total: combined.length,
      newInquiry: newInquiryCount,
      quoteSent: quoteSentCount,
      approved: approvedCount,
      scheduled: scheduledCount,
      totalValue
    });
  }, [quotes, appointments, bookings]);

  // Filter inquiries based on search
  const filteredInquiries = allInquiries.filter(inquiry => {
    if (!searchText) return true;
    return (
      inquiry.email.toLowerCase().includes(searchText.toLowerCase()) ||
      inquiry.phone.includes(searchText) ||
      inquiry.address.toLowerCase().includes(searchText.toLowerCase())
    );
  });

  // Group inquiries by status for Kanban columns
  const quotesByStatus = KANBAN_COLUMNS.reduce((acc, column) => {
    acc[column.id] = filteredInquiries.filter(inquiry => inquiry.status === column.id);
    return acc;
  }, {});

  // Get status color
  const getStatusColor = (status) => {
    const statusConfig = QUOTE_STATUSES.find(s => s.value === status);
    return statusConfig ? statusConfig.color : 'default';
  };

  // Handle inquiry status update (drag and drop)
  const handleStatusUpdate = async (inquiryId, newStatus) => {
    try {
      console.log('handleStatusUpdate called:', { inquiryId, newStatus });
      
      const inquiry = allInquiries.find(item => item.id === inquiryId);
      console.log('Found inquiry:', inquiry);
      
      if (!inquiry) {
        console.error('Inquiry not found:', inquiryId);
        message.error('Inquiry not found');
        return;
      }
      
      const previousStatus = inquiry?.status;
      let collectionName = 'quotes';
      if (inquiry?.type === 'appointment') {
        collectionName = 'appointments';
      } else if (inquiry?.type === 'booking') {
        collectionName = 'bookings';
      }
      
      const updateData = {
        updatedAt: serverTimestamp()
      };

      // Map kanban statuses to appointment statuses
      if (inquiry?.type === 'appointment') {
        updateData.status = newStatus === 'new_inquiry' ? 'scheduled' :
                           newStatus === 'quote_sent' ? 'confirmed' :
                           newStatus === 'approved' ? 'completed' :
                           newStatus === 'cancelled' ? 'cancelled' : 'scheduled';
      } else if (inquiry?.type === 'booking') {
        // Map kanban statuses to booking statuses
        updateData.status = newStatus === 'new_inquiry' ? 'pending' :
                           newStatus === 'quote_sent' ? 'contacted' :
                           newStatus === 'scheduled' ? 'scheduled' :
                           newStatus === 'approved' ? 'completed' :
                           newStatus === 'cancelled' ? 'cancelled' : 'pending';
      } else {
        // For quotes, use kanban status directly
        updateData.status = newStatus;
      }
      
      console.log('Updating document:', { collectionName, inquiryId, updateData });
      
      await updateDoc(doc(db, collectionName, inquiryId), updateData);
      
      console.log('Document updated successfully');
      
      // Track admin action
      trackAdminEvent(ANALYTICS_EVENTS.ADMIN_QUOTE_EDIT, {
        action: 'kanban_drag',
        inquiry_id: inquiryId,
        inquiry_type: inquiry?.type,
        new_status: newStatus,
        previous_status: previousStatus
      });
      
      const typeLabel = inquiry?.type === 'appointment' ? 'Appointment' : 
                       inquiry?.type === 'booking' ? 'Booking' : 
                       'Quote';
      message.success(`${typeLabel} moved successfully`);
    } catch (error) {
      console.error('Error updating inquiry:', error);
      message.error('Failed to update status');
    }
  };

  // Handle drag start
  const handleDragStart = (e, quoteId) => {
    e.dataTransfer.setData('text/plain', quoteId);
    e.currentTarget.classList.add('dragging');
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Handle drop
  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    const quoteId = e.dataTransfer.getData('text/plain');
    if (quoteId) {
      handleStatusUpdate(quoteId, newStatus);
    }
    // Remove dragging class from all cards
    document.querySelectorAll('.kanban-card.dragging').forEach(card => {
      card.classList.remove('dragging');
    });
  };

  // Handle quote deletion - show modal
  const handleDeleteQuote = (quoteId) => {
    console.log('handleDeleteQuote called with quoteId:', quoteId);
    
    // Find the inquiry in allInquiries to determine its type
    const inquiryToDelete = allInquiries.find(item => item.id === quoteId);
    
    console.log('inquiryToDelete:', inquiryToDelete);
    
    if (!inquiryToDelete) {
      console.error('Inquiry not found in allInquiries array');
      message.error('Inquiry not found');
      return;
    }
    
    setItemToDelete(inquiryToDelete);
    setDeleteModalVisible(true);
  };

  // Execute the actual deletion
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    console.log('Confirm delete clicked - starting delete process...');
    
    const quoteId = itemToDelete.id;
    
    // Determine type - check multiple indicators
    const hasCustomerEmail = itemToDelete.customerEmail || itemToDelete.email;
    const hasQuoteId = itemToDelete.quoteId;
    const typeFromField = itemToDelete.type;
    
    // More accurate type detection - prioritize quoteId over type field
    let isAppointment = false;
    if (hasQuoteId) {
      // If it has quoteId, it's definitely a quote
      isAppointment = false;
    } else if (typeFromField === 'appointment') {
      isAppointment = true;
    } else if (typeFromField === 'quote') {
      isAppointment = false;
    } else {
      // Fallback: if it has customerEmail but no quoteId, it's likely an appointment
      isAppointment = hasCustomerEmail && !hasQuoteId;
    }
    
    let collectionName = 'quotes';
    let itemType = 'Quote';
    
    if (typeFromField === 'booking') {
      collectionName = 'bookings';
      itemType = 'Booking';
    } else if (isAppointment) {
      collectionName = 'appointments';
      itemType = 'Appointment';
    }
    
    console.log('Type detection:', {
      typeFromField,
      hasCustomerEmail: !!hasCustomerEmail,
      hasQuoteId: !!hasQuoteId,
      isAppointment,
      collectionName
    });
    console.log('Deleting from collection:', collectionName, 'with ID:', quoteId);
    
    try {
      const docRef = doc(db, collectionName, quoteId);
      console.log('Document reference:', docRef.path);
      
      // Verify document exists before deleting
      console.log('Checking if document exists...');
      const docSnap = await getDoc(docRef);
      console.log('Document snapshot received, exists:', docSnap.exists());
      
      if (!docSnap.exists()) {
        console.error('Document does not exist:', docRef.path);
        message.error(`${itemType} not found in database`);
        setDeleteModalVisible(false);
        setItemToDelete(null);
        return;
      }
      
      console.log('Document exists, proceeding with delete...');
      await deleteDoc(docRef);
      console.log('Document deleted successfully from Firebase');
      
      // Track admin action
      trackAdminEvent(ANALYTICS_EVENTS.ADMIN_QUOTE_DELETE, {
        quote_id: quoteId,
        quote_status: itemToDelete?.status,
        quote_amount: itemToDelete?.quoteAmount,
        inquiry_type: itemToDelete?.type
      });
      
      message.success(`${itemType} deleted successfully`);
      setDeleteModalVisible(false);
      setItemToDelete(null);
    } catch (error) {
      console.error(`Error deleting ${itemType.toLowerCase()}:`, error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack,
        collectionName,
        quoteId
      });
      message.error(`Failed to delete ${itemType.toLowerCase()}: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle quote edit
  const handleEditQuote = (quote) => {
    setSelectedQuote(quote);
    editForm.setFieldsValue({
      ...quote,
      startDate: quote.startDate ? new Date(quote.startDate) : null
    });
    setModalVisible(true);
    
    // Track admin action
    trackAdminEvent(ANALYTICS_EVENTS.ADMIN_QUOTE_VIEW, {
      action: 'edit_modal_opened',
      quote_id: quote.id,
      quote_status: quote.status
    });
  };

  // Handle form submission
  const handleFormSubmit = async (values) => {
    try {
      await updateDoc(doc(db, 'quotes', selectedQuote.id), {
        ...values,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
        updatedAt: new Date()
      });
      message.success('Quote updated successfully');
      setModalVisible(false);
      setSelectedQuote(null);
    } catch (error) {
      console.error('Error updating quote:', error);
      message.error('Failed to update quote');
    }
  };


  // Export to CSV
  const handleExportCSV = () => {
    const csvContent = [
      ['Email', 'Phone', 'Address', 'Service Frequency', 'Start Date', 'Quote Amount', 'Status', 'Created Date'],
      ...filteredQuotes.map(quote => [
        quote.email,
        quote.phone,
        quote.address,
        quote.serviceFrequency,
        quote.startDate || '',
        quote.quoteAmount,
        quote.status,
        quote.createdAt.toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quotes-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    // Track admin action
    trackAdminEvent('csv_export', {
      quotes_count: filteredQuotes.length,
      export_date: new Date().toISOString()
    });
    
    message.success('CSV exported successfully');
  };

  // Handle view quote details
  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setViewModalVisible(true);
    
    // Track admin action
    trackAdminEvent(ANALYTICS_EVENTS.ADMIN_QUOTE_VIEW, {
      action: 'view_modal_opened',
      quote_id: quote.id,
      quote_status: quote.status
    });
  };

  // Kanban Card Component
  const KanbanCard = ({ quote }) => (
    <Card
      size="small"
      draggable
      onDragStart={(e) => handleDragStart(e, quote.id)}
      onDragEnd={handleDragEnd}
      onClick={() => handleViewQuote(quote)}
      className="kanban-card"
      style={{
        marginBottom: '12px',
        borderRadius: '8px',
        border: '1px solid #E8E8E8',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        cursor: 'pointer'
      }}
      bodyStyle={{ padding: '12px' }}
      hoverable
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Tag color={quote.type === 'appointment' ? 'blue' : 'green'} style={{ fontSize: '10px', margin: 0 }}>
              {quote.type === 'appointment' ? 'Appointment' : 'Quote'}
            </Tag>
            <Text strong style={{ fontSize: '14px', color: theme.colors.black, fontFamily: theme.fonts.primary }}>
              {quote.fullName || quote.email}
            </Text>
          </div>
          <div style={{ fontSize: '12px', color: theme.colors.gray }}>
            {quote.phone || 'No phone'}
          </div>
          {quote.email && quote.type === 'booking' && quote.fullName && (
            <div style={{ fontSize: '11px', color: theme.colors.gray, marginTop: '2px' }}>
              {quote.email}
            </div>
          )}
        </div>
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: 'View Details',
                icon: <EyeOutlined />
              },
              {
                key: 'edit',
                label: 'Edit',
                icon: <EditOutlined />
              },
              {
                type: 'divider'
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: <DeleteOutlined />,
                danger: true
              }
            ],
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              console.log('Menu item clicked:', key, 'quote:', quote);
              if (key === 'view') {
                handleViewQuote(quote);
              } else if (key === 'edit') {
                handleEditQuote(quote);
              } else if (key === 'delete') {
                console.log('Delete clicked, quote.id:', quote.id);
                handleDeleteQuote(quote.id);
              }
            }
          }}
          trigger={['click']}
        >
          <Button 
            type="text" 
            size="small" 
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      </div>
      
      {quote.address && quote.address !== 'Not provided' && (
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ fontSize: '12px', color: theme.colors.gray }}>
            <HomeOutlined style={{ marginRight: '4px' }} />
            {quote.address.length > 30 ? `${quote.address.substring(0, 30)}...` : quote.address}
          </Text>
        </div>
      )}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <Tag color={theme.colors.primary} style={{ fontSize: '11px' }}>
          {quote.type === 'booking' ? 'Booking Request' : 
           quote.serviceFrequency || (quote.services && quote.services.length > 0 ? quote.services.join(', ') : 'Service')}
        </Tag>
        {quote.quoteAmount > 0 && (
          <Text strong style={{ color: theme.colors.primary, fontSize: '14px' }}>
            {formatCurrency(quote.quoteAmount)}
          </Text>
        )}
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '11px', color: theme.colors.gray }}>
          <CalendarOutlined style={{ marginRight: '4px' }} />
          {formatDate(quote.createdAt)}
        </div>
        <div style={{ fontSize: '10px', color: theme.colors.gray, fontStyle: 'italic' }}>
          Click to view
        </div>
      </div>
    </Card>
  );


  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        {/* Admin Controls */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px',
          background: theme.colors.white,
          padding: '16px 24px',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.sm
        }}>
          <Title level={3} style={{ 
            margin: 0, 
            color: theme.colors.primary,
            fontFamily: theme.fonts.primary
          }}>
            HSP Admin - Kanban Board
          </Title>
          <Space>
            <Input
              placeholder="Search quotes..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: '250px', fontFamily: theme.fonts.primary }}
            />
            <Button 
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              style={{ fontFamily: theme.fonts.primary }}
            >
              Export CSV
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
              style={{ fontFamily: theme.fonts.primary }}
            >
              Refresh
            </Button>
          </Space>
        </div>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={12} sm={6}>
            <Card className="hsp-card">
              <Statistic
                title="New Inquiries"
                value={stats.newInquiry}
                prefix={<UserOutlined style={{ color: '#1890FF' }} />}
                valueStyle={{ color: '#1890FF', fontFamily: theme.fonts.primary }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="hsp-card">
              <Statistic
                title="Confirmed"
                value={stats.quoteSent}
                prefix={<ClockCircleOutlined style={{ color: '#FA8C16' }} />}
                valueStyle={{ color: '#FA8C16', fontFamily: theme.fonts.primary }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="hsp-card">
              <Statistic
                title="Approved"
                value={stats.approved}
                prefix={<CheckCircleOutlined style={{ color: '#52C41A' }} />}
                valueStyle={{ color: '#52C41A', fontFamily: theme.fonts.primary }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="hsp-card">
              <Statistic
                title="Scheduled"
                value={stats.scheduled}
                prefix={<CalendarOutlined style={{ color: '#722ED1' }} />}
                valueStyle={{ color: '#722ED1', fontFamily: theme.fonts.primary }}
              />
            </Card>
          </Col>
        </Row>

        {/* Kanban Board */}
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          overflowX: 'auto',
          paddingBottom: '16px'
        }}>
          {KANBAN_COLUMNS.map(column => (
            <div
              key={column.id}
              className="kanban-column"
              style={{
                minWidth: '300px',
                flex: 1,
                backgroundColor: column.bgColor,
                borderRadius: '12px',
                border: `2px solid ${column.borderColor}`,
                padding: '16px',
                minHeight: '600px'
              }}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* Column Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: `2px solid ${column.borderColor}`
              }}>
                <div>
                  <Title level={5} style={{ 
                    margin: 0, 
                    color: column.color,
                    fontFamily: theme.fonts.primary
                  }}>
                    {column.title}
                  </Title>
                  <Badge 
                    count={quotesByStatus[column.id]?.length || 0} 
                    style={{ backgroundColor: column.color }}
                  />
                </div>
                <DragOutlined style={{ color: column.color, fontSize: '16px' }} />
              </div>

              {/* Column Content */}
              <div style={{ minHeight: '500px' }}>
                {quotesByStatus[column.id]?.map(quote => (
                  <KanbanCard key={quote.id} quote={quote} />
                ))}
                {(!quotesByStatus[column.id] || quotesByStatus[column.id].length === 0) && (
                  <div style={{
                    textAlign: 'center',
                    color: theme.colors.gray,
                    padding: '40px 20px',
                    fontStyle: 'italic'
                  }}>
                    No inquiries in this column
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Edit Quote Modal */}
        <Modal
          title="Edit Quote"
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setSelectedQuote(null);
            editForm.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleFormSubmit}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ required: true, type: 'email' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Phone"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="address"
              label="Address"
              rules={[{ required: true }]}
            >
              <Input.TextArea rows={2} />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="serviceFrequency"
                  label="Service Frequency"
                  rules={[{ required: true }]}
                >
                  <Select>
                    {SERVICE_FREQUENCIES.map(freq => (
                      <Option key={freq.value} value={freq.value}>
                        {freq.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="startDate"
                  label="Start Date"
                >
                  <DatePicker style={{ width: '100%' }} suffixIcon={<CalendarOutlined />} getPopupContainer={(trigger) => trigger.parentElement} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="quoteAmount"
                  label="Quote Amount"
                  rules={[{ required: true, type: 'number' }]}
                >
                  <Input type="number" prefix="$" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true }]}
                >
                  <Select>
                    {QUOTE_STATUSES.map(status => (
                      <Option key={status.value} value={status.value}>
                        {status.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setModalVisible(false)}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  style={{ 
                    backgroundColor: theme.colors.primary,
                    borderColor: theme.colors.primary,
                    fontFamily: theme.fonts.primary
                  }}
                >
                  Update Quote
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* View Quote Details Modal */}
        <Modal
          title="Quote Details"
          open={viewModalVisible}
          onCancel={() => {
            setViewModalVisible(false);
            setSelectedQuote(null);
          }}
          footer={[
            <Button key="close" onClick={() => setViewModalVisible(false)}>
              Close
            </Button>,
            <Button 
              key="edit" 
              type="primary"
              onClick={() => {
                setViewModalVisible(false);
                handleEditQuote(selectedQuote);
              }}
              style={{ 
                backgroundColor: theme.colors.primary,
                borderColor: theme.colors.primary,
                fontFamily: theme.fonts.primary
              }}
            >
              Edit {selectedQuote?.type === 'booking' ? 'Booking' : selectedQuote?.type === 'appointment' ? 'Appointment' : 'Quote'}
            </Button>
          ]}
          width={800}
        >
          {selectedQuote && (
            <div>
              <Tabs defaultActiveKey="1">
                <TabPane tab="Customer Info" key="1">
                  <Row gutter={[16, 16]}>
                    {(selectedQuote.name || selectedQuote.fullName) && (
                      <Col span={24}>
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                            Name:
                          </Text>
                          <br />
                          <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                            {selectedQuote.fullName || selectedQuote.name}
                          </Text>
                        </div>
                      </Col>
                    )}
                    <Col span={12}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                          Email:
                        </Text>
                        <br />
                        <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                          {selectedQuote.email || 'Not provided'}
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                          Phone:
                        </Text>
                        <br />
                        <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                          {selectedQuote.phone}
                        </Text>
                      </div>
                    </Col>
                    {selectedQuote.address && selectedQuote.address !== 'Not provided' && (
                      <Col span={24}>
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                            Address:
                          </Text>
                          <br />
                          <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                            {selectedQuote.address}
                          </Text>
                        </div>
                      </Col>
                    )}
                    {selectedQuote.type === 'booking' && selectedQuote.source && (
                      <Col span={12}>
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                            Source:
                          </Text>
                          <br />
                          <Tag color="blue" style={{ marginTop: '4px' }}>
                            {selectedQuote.source}
                          </Tag>
                        </div>
                      </Col>
                    )}
                  </Row>
                  {selectedQuote.type === 'booking' && (selectedQuote.ipAddress || selectedQuote.location || selectedQuote.deviceInfo) && (
                    <Divider style={{ margin: '16px 0' }}>Additional Information</Divider>
                  )}
                  {selectedQuote.type === 'booking' && (
                    <Row gutter={[16, 16]}>
                      {selectedQuote.ipAddress && (
                        <Col span={12}>
                          <div style={{ marginBottom: '16px' }}>
                            <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                              IP Address:
                            </Text>
                            <br />
                            <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                              {selectedQuote.ipAddress}
                            </Text>
                          </div>
                        </Col>
                      )}
                      {selectedQuote.location && (
                        <Col span={12}>
                          <div style={{ marginBottom: '16px' }}>
                            <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                              Location:
                            </Text>
                            <br />
                            <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                              {selectedQuote.location.city && `${selectedQuote.location.city}, `}
                              {selectedQuote.location.region && `${selectedQuote.location.region}, `}
                              {selectedQuote.location.country || 'Unknown'}
                            </Text>
                          </div>
                        </Col>
                      )}
                      {selectedQuote.deviceInfo && (
                        <Col span={24}>
                          <div style={{ marginBottom: '16px' }}>
                            <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                              Device Info:
                            </Text>
                            <br />
                            <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary, fontSize: '12px' }}>
                              {selectedQuote.deviceInfo.deviceType} • {selectedQuote.deviceInfo.browser} • {selectedQuote.deviceInfo.os}
                            </Text>
                          </div>
                        </Col>
                      )}
                    </Row>
                  )}
                </TabPane>
                
                <TabPane tab={selectedQuote.type === 'booking' ? "Booking Details" : "Service Details"} key="2">
                  <Row gutter={[16, 16]}>
                    {selectedQuote.type !== 'booking' && selectedQuote.serviceFrequency && (
                      <Col span={12}>
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                            Service Frequency:
                          </Text>
                          <br />
                          <Tag color={theme.colors.primary} style={{ marginTop: '4px' }}>
                            {selectedQuote.serviceFrequency}
                          </Tag>
                        </div>
                      </Col>
                    )}
                    <Col span={12}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                          Start Date:
                        </Text>
                        <br />
                        <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                          {selectedQuote.startDate || selectedQuote.preferredDate || selectedQuote.date || 'Not specified'}
                        </Text>
                      </div>
                    </Col>
                    {(selectedQuote.preferredTime || selectedQuote.time) && (
                      <Col span={12}>
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                            Preferred Time:
                          </Text>
                          <br />
                          <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                            {selectedQuote.preferredTime || selectedQuote.time}
                          </Text>
                        </div>
                      </Col>
                    )}
                    <Col span={12}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                          Quote Amount:
                        </Text>
                        <br />
                        <Text strong style={{ color: theme.colors.primary, fontSize: '18px', fontFamily: theme.fonts.primary }}>
                          ${selectedQuote.quoteAmount || selectedQuote.totalPrice || 0}
                        </Text>
                        {selectedQuote.quoteType === 'campaign_special' && (
                          <div style={{ marginTop: '4px' }}>
                            <Text style={{ fontSize: '12px', color: theme.colors.gray }}>
                              Base: ${selectedQuote.basePrice || 99}
                              {selectedQuote.totalPrice > (selectedQuote.basePrice || 99) && (
                                <span> + Add-ons: ${selectedQuote.totalPrice - (selectedQuote.basePrice || 99)}</span>
                              )}
                            </Text>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                          Status:
                        </Text>
                        <br />
                        <Tag color={getStatusColor(selectedQuote.status)} style={{ marginTop: '4px' }}>
                          {selectedQuote.status.toUpperCase()}
                        </Tag>
                      </div>
                    </Col>
                  </Row>
                  
                  <Divider />
                  
                  <div>
                    <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                      Selected Services:
                    </Text>
                    <div style={{ marginTop: '8px' }}>
                      {(() => {
                        // Handle different service data formats
                        const services = [];
                        
                        // Check if selectedServices is an object (new format)
                        if (selectedQuote.selectedServices && typeof selectedQuote.selectedServices === 'object') {
                          Object.entries(selectedQuote.selectedServices).forEach(([serviceId, isSelected]) => {
                            if (isSelected) {
                              // Try to find service in exteriorServices or interiorServices arrays
                              const service = [...(selectedQuote.exteriorServices || []), ...(selectedQuote.interiorServices || [])]
                                .find(s => s.id === serviceId);
                              
                              if (service) {
                                services.push(service);
                              } else {
                                // Fallback: create service object from ID
                                const serviceNames = {
                                  'gutter-cleaning': 'Gutter Inspection & Cleaning',
                                  'weed-pulling': 'Weed Pulling',
                                  'solar-cleaning': 'Solar Panel Cleaning',
                                  'touchup-painting': 'Exterior Touch-up Painting',
                                  'pressure-washing': 'Pressure Washing',
                                  'deck-cleaning': 'Deck Cleaning'
                                };
                                services.push({
                                  id: serviceId,
                                  name: serviceNames[serviceId] || serviceId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                });
                              }
                            }
                          });
                        }
                        
                        // Check if exteriorServices array exists (campaign format)
                        if (selectedQuote.exteriorServices && Array.isArray(selectedQuote.exteriorServices)) {
                          selectedQuote.exteriorServices.forEach(service => {
                            if (!services.find(s => s.id === service.id)) {
                              services.push(service);
                            }
                          });
                        }
                        
                        // If no services found, show a message
                        if (services.length === 0) {
                          return (
                            <Text style={{ color: theme.colors.gray, fontStyle: 'italic' }}>
                              No services selected
                            </Text>
                          );
                        }
                        
                        return services.map(service => (
                          <Tag key={service.id} color={theme.colors.accent} style={{ margin: '4px', color: theme.colors.black }}>
                            {service.name}
                          </Tag>
                        ));
                      })()}
                    </div>
                  </div>
                </TabPane>
                
                <TabPane tab="Property Info" key="3">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                          Square Footage:
                        </Text>
                        <br />
                        <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                          {selectedQuote.propertyData?.sqft || 'Not specified'} sq ft
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                          Lot Size:
                        </Text>
                        <br />
                        <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                          {selectedQuote.propertyData?.lotSize || 'Not specified'} sq ft
                        </Text>
                      </div>
                    </Col>
                    {(selectedQuote.specialInstructions || selectedQuote.instructions) && (
                      <Col span={24}>
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                            Special Instructions:
                          </Text>
                          <br />
                          <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                            {selectedQuote.specialInstructions || selectedQuote.instructions}
                          </Text>
                        </div>
                      </Col>
                    )}
                    {selectedQuote.quoteType === 'campaign_special' && (
                      <Col span={24}>
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                            Campaign Type:
                          </Text>
                          <br />
                          <Tag color="orange" style={{ marginTop: '4px' }}>
                            {selectedQuote.campaignPage || 'Campaign Special'}
                          </Tag>
                        </div>
                      </Col>
                    )}
                    {selectedQuote.utmCampaign && (
                      <Col span={24}>
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                            UTM Campaign:
                          </Text>
                          <br />
                          <div style={{ marginTop: '4px' }}>
                            <Tag color="blue" style={{ marginRight: '4px' }}>
                              Source: {selectedQuote.utmSource}
                            </Tag>
                            <Tag color="green" style={{ marginRight: '4px' }}>
                              Medium: {selectedQuote.utmMedium}
                            </Tag>
                            <Tag color="purple" style={{ marginRight: '4px' }}>
                              Campaign: {selectedQuote.utmCampaign}
                            </Tag>
                            {selectedQuote.utmTerm && (
                              <Tag color="orange">
                                Term: {selectedQuote.utmTerm}
                              </Tag>
                            )}
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </TabPane>
                
                <TabPane tab="Timeline" key="4">
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                      Created:
                    </Text>
                    <br />
                    <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                      {selectedQuote.createdAt.toLocaleString()}
                    </Text>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                      Last Updated:
                    </Text>
                    <br />
                    <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                      {selectedQuote.updatedAt.toLocaleString()}
                    </Text>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                      Quote ID:
                    </Text>
                    <br />
                    <Text style={{ color: theme.colors.gray, fontFamily: 'monospace' }}>
                      {selectedQuote.id}
                    </Text>
                  </div>
                </TabPane>
              </Tabs>
            </div>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          title={itemToDelete ? `Delete ${itemToDelete.type === 'appointment' ? 'Appointment' : 'Quote'}` : 'Delete Item'}
          open={deleteModalVisible}
          onOk={confirmDelete}
          onCancel={() => {
            setDeleteModalVisible(false);
            setItemToDelete(null);
          }}
          okText="Delete"
          okType="danger"
          cancelText="Cancel"
        >
          {itemToDelete && (
            <div>
              <p>Are you sure you want to delete this {itemToDelete.type === 'appointment' ? 'appointment' : 'quote'}? This action cannot be undone.</p>
              <p style={{ marginTop: '16px', color: theme.colors.gray }}>
                <strong>Email:</strong> {itemToDelete.email || itemToDelete.customerEmail}<br />
                <strong>ID:</strong> {itemToDelete.id}
              </p>
            </div>
          )}
        </Modal>
    </div>
  );
};

export default AdminDashboard;
